import {
  syncDelta,
  syncFull,
  clearOfflineData,
  getPendingOpsCount,
  cleanOldSyncedOps,
} from "@/services/syncService";
import { flushQueue, getPendingCount } from "@/services/offlineQueue";
import { OFFLINE_SYNC_TABLES } from "@/config/offlineConfig";
import { TAMBO_LITE_MODE } from "@/config/liteBeta";

export default {
  namespaced: true,

  state: {
    isOnline: navigator.onLine,
    pendingCount: 0,
    syncing: false,
    lastSyncTime: localStorage.getItem("tambopal_last_sync_at") || null,
  },

  getters: {
    isOnline: (s) => s.isOnline,
    isOffline: (s) => !s.isOnline,
    pendingCount: (s) => s.pendingCount,
    syncing: (s) => s.syncing,
    lastSyncTime: (s) => s.lastSyncTime,
    hasPendingOps: (s) => s.pendingCount > 0,
  },

  mutations: {
    setOnline(state, value) {
      state.isOnline = value;
    },
    setPendingCount(state, count) {
      state.pendingCount = count;
    },
    setSyncing(state, value) {
      state.syncing = value;
    },
    setLastSyncTime(state, time) {
      state.lastSyncTime = time;
    },
  },

  actions: {
    /**
     * Inicializa los listeners de online/offline y carga el estado inicial.
     */
    init({ commit, dispatch }) {
      commit("setOnline", navigator.onLine);
      if (TAMBO_LITE_MODE) {
        dispatch("refreshPendingCount");
        return;
      }

      window.addEventListener("online", () => {
        commit("setOnline", true);
        console.log("🌐 Conexión recuperada");
        // Al reconectar: enviar operaciones pendientes y luego sincronizar
        dispatch("flushAndSync");
      });

      window.addEventListener("offline", () => {
        commit("setOnline", false);
        console.log("📴 Conexión perdida - Modo offline");
      });

      // Cargar contador de pendientes
      dispatch("refreshPendingCount");
    },

    /**
     * Sincronización delta: descarga cambios desde la última vez.
     */
    async syncDelta({ commit, state }) {
      if (TAMBO_LITE_MODE) return false;
      if (!state.isOnline) return false;
      commit("setSyncing", true);
      try {
        const ok = await syncDelta(OFFLINE_SYNC_TABLES);
        if (ok) {
          commit(
            "setLastSyncTime",
            localStorage.getItem("tambopal_last_sync_at")
          );
        }
        return ok;
      } finally {
        commit("setSyncing", false);
      }
    },

    /**
     * Sincronización completa: descarga todo desde cero.
     */
    async syncFull({ commit, state }) {
      if (TAMBO_LITE_MODE) return false;
      if (!state.isOnline) return false;
      commit("setSyncing", true);
      try {
        const ok = await syncFull(OFFLINE_SYNC_TABLES);
        if (ok) {
          commit(
            "setLastSyncTime",
            localStorage.getItem("tambopal_last_sync_at")
          );
        }
        return ok;
      } finally {
        commit("setSyncing", false);
      }
    },

    /**
     * Enviar operaciones pendientes y luego hacer delta sync.
     */
    async flushAndSync({ dispatch, commit, state }) {
      if (TAMBO_LITE_MODE) return;
      if (!state.isOnline) return;

      try {
        const sent = await flushQueue();
        if (sent > 0) {
          console.log(`📤 ${sent} operaciones sincronizadas`);
        }
      } catch (err) {
        console.warn("⚠️ Error en flush:", err);
      }

      await dispatch("refreshPendingCount");
      await dispatch("syncDelta");

      // Limpiar ops antiguas
      await cleanOldSyncedOps();
    },

    /**
     * Actualizar el contador de operaciones pendientes.
     */
    async refreshPendingCount({ commit }) {
      const count = await getPendingCount();
      commit("setPendingCount", count);
    },

    /**
     * Limpiar todos los datos offline (logout / cambiar tambo).
     */
    async clearAll({ commit }) {
      await clearOfflineData();
      commit("setPendingCount", 0);
      commit("setLastSyncTime", null);
    },
  },
};
